import type { Product } from '@agos/api-client/model'

/**
 * The add-ons a product can offer, under the category each was filed in — FRONTEND.md §9.227.
 *
 * *"also Doplnky k produktu should be better visible, I hate how its now, its not categorized."*
 * Legacy groups them by `categoryName`, falling back to *Nezaradené*, and prints a grey bar per
 * group. AGOS drew one flat list in whatever order the endpoint answered.
 *
 * **And the list was cut off at sixty.** This data has **164 active products in 18 categories**, so
 * 104 of them could not be reached at all unless somebody guessed a name to search for — with nothing
 * on screen saying anything was missing. Legacy renders every one inside a scroll box. The cap is
 * gone; the box still scrolls.
 */

/**
 * Legacy's own fallback heading, and a **source string for the translator** rather than a rendered
 * one: `AddonSelector` draws every category through `<T>{group.category}</T>`, exactly as it draws
 * the names a franchise typed. `generated/nav-tree.ts` carries sixty-two labels the same way.
 */
// eslint-disable-next-line no-restricted-syntax -- rendered through `<T>` at the render site, above
export const UNCATEGORISED_ADDONS = 'Nezaradené'

export interface AddonGroup {
  category: string
  products: Product[]
}

/**
 * Grouped, and in a stable order.
 *
 * Categories are ordered by name so the list does not rearrange itself between openings, with
 * *Nezaradené* last — it is the absence of a category rather than one of them.
 */
export function addonGroups(
  products: readonly Product[] | undefined,
  excludeProductId: number | null,
  search: string,
): AddonGroup[] {
  const query = search.trim().toLowerCase()
  const groups = new Map<string, Product[]>()

  for (const product of products ?? []) {
    // A product is not its own add-on.
    if (excludeProductId !== null && product.id === excludeProductId) continue
    if (query !== '' && !(product.name ?? '').toLowerCase().includes(query)) continue

    const name = (product.categoryName ?? '').trim()
    const key = name === '' ? UNCATEGORISED_ADDONS : name
    const bucket = groups.get(key)
    if (bucket) bucket.push(product)
    else groups.set(key, [product])
  }

  return [...groups.entries()]
    .map(([category, entries]) => ({
      category,
      // Within a group, by name — the endpoint's order is the menu's, which is meaningless here.
      products: [...entries].sort((left, right) =>
        (left.name ?? '').localeCompare(right.name ?? '', 'sk'),
      ),
    }))
    .sort((left, right) => {
      if (left.category === UNCATEGORISED_ADDONS) return 1
      if (right.category === UNCATEGORISED_ADDONS) return -1
      return left.category.localeCompare(right.category, 'sk')
    })
}

/** How many are on offer at all, which is what tells somebody the search found nothing. */
export function addonCount(groups: readonly AddonGroup[]): number {
  return groups.reduce((total, group) => total + group.products.length, 0)
}
